/**
 * Terrain as causes (decision 38; BUILDLIST 62.11, R13). Every change a match makes to a planet's
 * terrain is a **crater** or a **mound** at a point with a radius (and a depth multiplier), applied in
 * order to the generated surface. `resolveImpact` records each one as a `TerrainEdit`; the match carries
 * the log in `state.terrainEdits` (left out of the per-tick checksum — it decides nothing, the heightmap
 * it describes does); a sector battle's outcome carries it beside the scar logs.
 *
 * Replaying the log on a freshly generated planet with the same functions gives the scarred planet
 * back **bit for bit** on the JS crater path (tests/terrainEdits.test.ts), so the log is a durable
 * record of the scars that a change of mesh can re-apply (R13): a crater is a place and a size, not a
 * list of vertex indices. The kernel's crater agrees with JS since R5.
 */
import { withCraterlet } from './craterlets';
import { withTerrace } from './terraceGround';
import type { Dig, GameState, PlanetState, TerrainEdit, Vec3 } from '../types';
import { vecAdd, vecScale } from '../math';
import type { GroundMaterial } from '../types/ground';
import { createCrater, createMound, CRATER_DEPTH_FACTOR } from './damage';
import { CRATER_DEPTH_CAP_PERSONS } from '../constants';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { clockPolicyFor } from '../match/clockPolicy';
import { dacos, dcos } from './dmath';
import { verticesNear } from './vertexCells';

/** Apply one recorded edit (no colours: this is the sim's terrain, not the mesh's paint). */
export function applyTerrainEdit(planet: PlanetState, e: TerrainEdit): PlanetState {
  if (e.kind === 'dig') return e.dig ? withDig(planet, e.dig) : planet;   // 66.8
  if (e.kind === 'craterlet') return e.craterlet ? withCraterlet(planet, e.craterlet) : planet;   // 63.11
  if (e.kind === 'terrace') return e.terrace ? withTerrace(planet, e.terrace) : planet;   // 63.14 2b
  if (e.kind === 'mound') return createMound(planet, e.point, e.radius).planet;
  const c = createCrater(planet, e.point, e.radius, e.depth);
  const floored = e.floor === undefined ? c.planet : floorCrater(c.planet, c.modifiedVertices, e.floor).planet;
  const rimmed = e.rim ? rimCrater(floored, e.point, e.radius, e.depth, e.rim).planet : floored;   // 63.5 step 3
  return craterletsUnder(digsUnder(rimmed, e.point, e.radius), e.point, e.radius);   // 66.8: a shell ends the digs under it; 63.11: and the craterlets
}

/** 66.8 (§5.18): `planet` with `dig` dug — it replaces an earlier record of the same dig (a deepening, a dig left part-dug). */
export function withDig(planet: PlanetState, dig: Dig): PlanetState {
  return { ...planet, digs: [...(planet.digs ?? []).filter(d => d.id !== dig.id), dig] };
}

/** 66.8 (§5.18): a crater at `point` (world) of `radius` ends every dig whose centre is within its reach. The planet as it was when none is. */
export function digsUnder(planet: PlanetState, point: Vec3, radius: number): PlanetState {
  if (!planet.digs?.length) return planet;
  const px = point.x - planet.position.x, py = point.y - planet.position.y, pz = point.z - planet.position.z;
  const pl = Math.sqrt(px * px + py * py + pz * pz) || 1;
  const cosReach = dcos(radius / planet.radius);
  const kept = planet.digs.filter(d => (d.dir.x * px + d.dir.y * py + d.dir.z * pz) / pl < cosReach);
  return kept.length === planet.digs.length ? planet : { ...planet, digs: kept };
}

/** 63.11 (§5.18): a crater at `point` (world) of `radius` ends every craterlet whose centre is within its reach. The planet as it was when none is. */
export function craterletsUnder(planet: PlanetState, point: Vec3, radius: number): PlanetState {
  if (!planet.craterlets?.length) return planet;
  const px = point.x - planet.position.x, py = point.y - planet.position.y, pz = point.z - planet.position.z;
  const pl = Math.sqrt(px * px + py * py + pz * pz) || 1;
  const cosReach = dcos(radius / planet.radius);
  const kept = planet.craterlets.filter(c => (c.dir.x * px + c.dir.y * py + c.dir.z * pz) / pl < cosReach);
  return kept.length === planet.craterlets.length ? planet : { ...planet, craterlets: kept };
}

/** 66.8 (§5.18): the log's record of a dig as it ended. */
export function digEdit(planetIndex: number, planet: PlanetState, dig: Dig): TerrainEdit {
  return { kind: 'dig', planetIndex, planetId: planet.id, point: vecAdd(planet.position, vecScale(dig.dir, planet.radius)), radius: 0, depth: 0, material: dig.material, dig: { ...dig, dir: { ...dig.dir }, facing: { ...dig.facing } } };
}

/**
 * 63.5 step 3: a crater's rim — the ground just outside its bowl raised, from its edge (1 radius) out to its throw (1.4),
 * highest at 1.15 radii: `rim` × the bowl's depth at its centre (`radius` × CRATER_DEPTH_FACTOR × `depthMultiplier`, as
 * createCrater digs it). A pass after the dig, as floorCrater is: the kernel digs the bowl from the same arguments, and
 * the Match syncs every terrain write to it. The rim adds no mass (it is the crater's own throw). `raised` are the vertices
 * it moved (their colours and the mesh need repainting). The same planet when none moved.
 */
export function rimCrater(planet: PlanetState, impactWorld: Vec3, radius: number, depthMultiplier: number, rim: number): { planet: PlanetState; raised: number[] } {
  if (!(rim > 0) || planet.indestructible) return { planet, raised: [] };
  const ix = impactWorld.x - planet.position.x, iy = impactWorld.y - planet.position.y, iz = impactWorld.z - planet.position.z;
  const il = Math.sqrt(ix * ix + iy * iy + iz * iz) || 1;
  const angR = radius / planet.radius, reach = 1.4 * angR, cosReach = reach >= Math.PI ? -2 : dcos(reach) - 1e-9;
  const lift = rim * radius * CRATER_DEPTH_FACTOR * depthMultiplier;
  const bp = planet.basePositions;
  const raised: number[] = [];
  let heights: Float32Array | null = null;
  // (the vertices within the throw, ascending — the whole-planet scan's order and arithmetic: core/vertexCells.ts)
  const near = verticesNear(bp, { x: ix / il, y: iy / il, z: iz / il }, reach), count = near ? near.length : planet.heightmap.length;
  for (let k = 0; k < count; k++) {
    const i = near ? near[k] : k;
    const bx = bp[i * 3], by = bp[i * 3 + 1], bz = bp[i * 3 + 2];
    const bl = Math.sqrt(bx * bx + by * by + bz * bz) || 1;
    const c = (bx * ix + by * iy + bz * iz) / (bl * il);
    if (c < cosReach) continue;
    const t = dacos(Math.min(1, Math.max(-1, c))) / angR;
    if (t < 1 || t >= 1.4) continue;
    const bump = t < 1.15 ? (t - 1) / 0.15 : (1.4 - t) / 0.25;
    if (!(bump > 0)) continue;
    heights ??= new Float32Array(planet.heightmap);
    heights[i] = planet.heightmap[i] + lift * bump;
    raised.push(i);
  }
  return heights ? { planet: { ...planet, heightmap: heights }, raised } : { planet, raised };
}

// ---- Decision 134: a person-scale crater's depth, capped ------------------------------------------------------------

/**
 * How deep a crater may dig below the generated ground (world units): CRATER_DEPTH_CAP_PERSONS person heights under the
 * clock policy's `craterDepth: 'capped'` (the action clock) at person scale; null otherwise — at classic, and in the
 * turn game, whose human profile must replay the classic golden exactly (SCALE.md rule 1 whole). Read once per impact:
 * resolveImpact passes it to the crater's depth and the floor pass alike.
 */
export function craterDepthCap(state: Pick<GameState, 'config'>): number | null {
  if (clockPolicyFor(state.config).craterDepth !== 'capped') return null;
  const ps = personSetOf(state);
  return ps.k < 1 ? CRATER_DEPTH_CAP_PERSONS * PERSON_BODY_HEIGHT * ps.k : null;
}

/** The depth multiplier that keeps one crater of `radius` no deeper than `cap` at its centre — the same bowl, shallower. */
export function cappedCraterDepth(depthMultiplier: number, radius: number, cap: number | null): number {
  return cap === null ? depthMultiplier : Math.min(depthMultiplier, cap / (CRATER_DEPTH_FACTOR * radius));
}

/**
 * Shells landing on one spot stack their bowls: lift any of `vertices` dug deeper than `cap` below the generated ground
 * (the heightmap the first crater pinned) back to that floor — so the pit saturates as a foxhole. `lifted` are the
 * vertices it moved (their colours need repainting). The same planet when none moved.
 */
export function floorCrater(planet: PlanetState, vertices: readonly number[], cap: number): { planet: PlanetState; lifted: number[] } {
  const ground = planet.initialHeightmap ?? planet.heightmap;
  const lifted: number[] = [];
  let heights: Float32Array | null = null;
  for (const i of vertices) {
    const floor = ground[i] - cap;
    if (planet.heightmap[i] < floor) { heights ??= new Float32Array(planet.heightmap); heights[i] = floor; lifted.push(i); }
  }
  return heights ? { planet: { ...planet, heightmap: heights }, lifted } : { planet, lifted };
}

/** Replay a log on a planet, in order. Edits for other planets are skipped. */
export function rebuildPlanet(generated: PlanetState, edits: readonly TerrainEdit[]): PlanetState {
  let p = generated;
  for (const e of edits) if (e.planetId === generated.id) p = applyTerrainEdit(p, e);
  return p;
}

/** Replay a log on a system's freshly generated planets. */
export function rebuildPlanets(generated: PlanetState[], edits: readonly TerrainEdit[]): PlanetState[] {
  return generated.map(p => rebuildPlanet(p, edits));
}

/** A crater edit record (`floor`: decision 134's cap, at person scale; `material`: 63.5 step 2's ground it was dug in; `rim`: step 3's, when it raised one). */
export function craterEdit(planetIndex: number, planet: PlanetState, point: Vec3, radius: number, depth: number, floor: number | null = null, material?: GroundMaterial, rim = 0): TerrainEdit {
  return { kind: 'crater', planetIndex, planetId: planet.id, point: { x: point.x, y: point.y, z: point.z }, radius, depth, ...(floor !== null ? { floor } : {}), ...(material ? { material } : {}), ...(rim > 0 ? { rim } : {}) };
}

/** 63.5 step 2: an edit's ground material — an older log without one reads soil. */
export function editMaterial(e: Pick<TerrainEdit, 'material'>): GroundMaterial {
  return e.material ?? 'soil';
}

/** A mound edit record (the terraformer). */
export function moundEdit(planetIndex: number, planet: PlanetState, point: Vec3, radius: number): TerrainEdit {
  return { kind: 'mound', planetIndex, planetId: planet.id, point: { x: point.x, y: point.y, z: point.z }, radius, depth: 1 };
}

/** Whether two heightmaps are the same, bit for bit. */
export function sameHeights(a: Float32Array, b: Float32Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/** The largest height difference between two heightmaps (Infinity when their lengths differ). */
export function maxHeightDiff(a: Float32Array, b: Float32Array): number {
  if (a.length !== b.length) return Infinity;
  let m = 0;
  for (let i = 0; i < a.length; i++) m = Math.max(m, Math.abs(a[i] - b[i]));
  return m;
}
